import { config } from "../config";
import { normalizeIndianMobile } from "./login";
import { humanRefused, withHuman } from "../auth/human";

/**
 * Operator self sign-up, the first step of becoming a provider:
 * POST /access/operator/signup (radaccess v1.53+).
 *
 * The MSG91 widget proves the mobile in the browser; radaccess confirms the
 * widget's token with MSG91 server-side, creates the provider's login
 * (username = the E.164 mobile, phone verified) and answers 201. Owner plan
 * 2026-09-13: mobile first, sign in straight away, compliance details after.
 *
 * ⛔ PUBLIC AND UNAUTHENTICATED, like the sign-in routes: no token and no
 * namespace are sent, because a person signing up has neither. The password
 * travels in the JSON body over TLS and nowhere else -- not in the URL, not in
 * storage, not in a log line.
 *
 * 🔑 THE FORM RULES MIRROR radaccess (handler/operatorsignup.go) BYTE FOR BYTE.
 * radaccess validates before it spends MSG91's check, but the widget's code
 * has already been used by then; checking here first means a typo in the
 * password never costs the person a fresh code.
 */

export interface SignupForm {
  firstName: string;
  lastName: string;
  mobile: string;
  /** A name to sign in with, besides the mobile (owner 2026-10-30). */
  username: string;
  password: string;
  confirm: string;
}

export const EMPTY_SIGNUP_FORM: SignupForm = { firstName: "", lastName: "", mobile: "", username: "", password: "", confirm: "" };

/** Limits radaccess enforces; lengths are UTF-8 bytes, as Go's len() counts them. */
export const NAME_MAX_BYTES = 100;
export const PASSWORD_MIN_BYTES = 8;
export const PASSWORD_MAX_BYTES = 72;

const byteLength = (s: string): number => new TextEncoder().encode(s).length;

/**
 * ZITADEL's complexity rule as radaccess applies it: ASCII upper, lower and
 * digit, and anything that is none of those counts as a symbol.
 */
export function passwordProblems(password: string): string[] {
  const p: string[] = [];
  const bytes = byteLength(password);
  if (bytes < PASSWORD_MIN_BYTES) p.push(`Password must be at least ${PASSWORD_MIN_BYTES} characters.`);
  if (bytes > PASSWORD_MAX_BYTES) p.push(`Password must be at most ${PASSWORD_MAX_BYTES} bytes.`);
  let upper = false;
  let lower = false;
  let digit = false;
  let symbol = false;
  for (const ch of password) {
    if (ch >= "A" && ch <= "Z") upper = true;
    else if (ch >= "a" && ch <= "z") lower = true;
    else if (ch >= "0" && ch <= "9") digit = true;
    else symbol = true;
  }
  if (!upper) p.push("Password needs an upper-case letter.");
  if (!lower) p.push("Password needs a lower-case letter.");
  if (!digit) p.push("Password needs a number.");
  if (!symbol) p.push("Password needs a symbol.");
  return p;
}

/** Mirrors radaccess zitadel.ValidChosenUserName. "" when it is a good username. */
export function usernameProblem(typed: string): string {
  const s = typed.trim().toLowerCase();
  if (s.length < 4) return "A username has at least 4 characters.";
  if (s.length > 30) return "A username has at most 30 characters.";
  if (!/^[a-z][a-z0-9._-]{3,29}$/.test(s)) return "A username starts with a letter and has only letters, digits, . _ and -.";
  return "";
}

/** Everything wrong with the form, or [] when it may be sent. */
export function signupFormProblems(f: SignupForm): string[] {
  const p: string[] = [];
  const first = f.firstName.trim();
  const last = f.lastName.trim();
  if (!first) p.push("Enter your first name.");
  if (!last) p.push("Enter your last name.");
  if (byteLength(first) > NAME_MAX_BYTES || byteLength(last) > NAME_MAX_BYTES) {
    p.push("First and last name must each be at most 100 characters.");
  }
  if (normalizeIndianMobile(f.mobile) === null) p.push("Enter a 10-digit Indian mobile number.");
  const u = usernameProblem(f.username);
  if (u) p.push(u);
  // Onboarding collects every sign-in detail (owner 2026-10-30): Google and an
  // authenticator stay the operator's option; the password does not.
  if (f.password === "") p.push("Choose a password.");
  else p.push(...optionalPasswordProblems(f.password, f.confirm));
  return p;
}

/**
 * Password optional (owner 2026-10-24, radaccess v1.161): blank means the
 * person signs in with their mobile and a texted code; radaccess gives the
 * login a random password nobody sees. Anything typed must still be a good one.
 */
export function optionalPasswordProblems(password: string, confirm: string): string[] {
  if (password === "" && confirm === "") return [];
  const p = passwordProblems(password);
  if (password !== confirm) p.push("The two passwords do not match.");
  return p;
}

export interface SignupRequest {
  firstName: string;
  lastName: string;
  mobile: string;
  username?: string;
  password: string;
  /** The access token MSG91's widget returned for this mobile. */
  widgetToken: string;
}

export type UsernameCheck = { kind: "available" } | { kind: "taken" } | { kind: "invalid"; message: string } | { kind: "unavailable" };

/** Is this username free? Asked before any code is sent (owner 2026-10-30). */
export async function checkSignupUsername(username: string): Promise<UsernameCheck> {
  try {
    const res = await fetch(`${config().gatewayBase}/operator/signup/username`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ username }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.status === 200 && body.available === true) return { kind: "available" };
    if (res.status === 200 && body.available === false) return { kind: "taken" };
    if (res.status === 400 && typeof body.message === "string") return { kind: "invalid", message: body.message };
    return { kind: "unavailable" };
  } catch {
    return { kind: "unavailable" };
  }
}

export type SignupTestCodeSend = { kind: "sent"; requestId: string; resendIn: number } | { kind: "refused"; message: string };

/**
 * A code for a TEST mobile: written to the test operator's SMS log, never
 * texted. The page then sends requestId.code as the widget token.
 */
export async function sendSignupTestCode(mobile: string): Promise<SignupTestCodeSend> {
  const e164 = normalizeIndianMobile(mobile);
  if (e164 === null) return { kind: "refused", message: "Enter a mobile number." };
  try {
    const res = await fetch(`${config().gatewayBase}/operator/signup/testcode`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ mobile: e164 }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.status === 200 && typeof body.requestId === "string" && body.requestId) {
      return { kind: "sent", requestId: body.requestId, resendIn: typeof body.resendIn === "number" ? body.resendIn : 30 };
    }
    if (res.status === 429) return { kind: "refused", message: "Wait a moment before asking for another code." };
    return { kind: "refused", message: typeof body.error === "string" ? body.error : "Could not make a test code." };
  } catch {
    return { kind: "refused", message: "Could not make a test code." };
  }
}

export type SignupOutcome =
  /** 201: the login exists; sign in with `username` and the password. */
  | { kind: "created"; username: string; applicationId: string; note: string }
  /** 400/422: radaccess refused the form, with its reasons. */
  | { kind: "incomplete"; problems: string[] }
  /** 401: the widget token did not prove this mobile, or was already used. */
  | { kind: "not_verified" }
  /** 409: this mobile already has an account. */
  | { kind: "exists" }
  /** 409: the chosen username is taken; the mobile must be verified again. */
  | { kind: "username_taken" }
  /** 429: MSG91 or the gateway is throttling; the same token may be retried. */
  | { kind: "busy"; retryAfter: number }
  /** 502 "partly created": support has to finish it; do not sign up again. */
  | { kind: "partial"; message: string }
  /** 503, 5xx or no answer: nothing is known to have been created. */
  | { kind: "unavailable"; status: number | null };

const text = (v: unknown): string => (typeof v === "string" ? v : "");

export async function signUp(input: SignupRequest, signal?: AbortSignal): Promise<SignupOutcome> {
  const mobile = normalizeIndianMobile(input.mobile);
  if (mobile === null) return { kind: "incomplete", problems: ["Enter a 10-digit Indian mobile number."] };
  const widgettoken = input.widgetToken.trim();
  if (!widgettoken) return { kind: "not_verified" };

  let res: Response;
  try {
    res = await fetch(`${config().gatewayBase}/operator/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(withHuman("/operator/signup", {
        mobile,
        widgettoken,
        password: input.password,
        firstname: input.firstName.trim(),
        lastname: input.lastName.trim(),
        ...(input.username?.trim() ? { username: input.username.trim() } : {}),
      })),
      ...(signal ? { signal } : {}),
    });
  } catch {
    return { kind: "unavailable", status: null };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  humanRefused("/operator/signup", res.status, body);

  switch (res.status) {
    case 201:
      return {
        kind: "created",
        username: text(body.username) || mobile,
        applicationId: text(body.applicationid),
        note: text(body.note),
      };
    case 400:
    case 422: {
      const listed = Array.isArray(body.problems)
        ? body.problems.filter((x): x is string => typeof x === "string")
        : [];
      return { kind: "incomplete", problems: listed.length > 0 ? listed : [text(body.error) || "The sign-up is incomplete."] };
    }
    case 401:
      return { kind: "not_verified" };
    case 409:
      return text(body.error) === "username_taken" ? { kind: "username_taken" } : { kind: "exists" };
    case 429: {
      // Retry-After is not a CORS-safelisted response header, so a browser may
      // not expose it; a minute is what radaccess sends.
      const after = Number(res.headers.get("Retry-After"));
      return { kind: "busy", retryAfter: Number.isFinite(after) && after > 0 ? after : 60 };
    }
    case 502: {
      const message = text(body.error);
      if (/partly created/i.test(message)) return { kind: "partial", message };
      return { kind: "unavailable", status: 502 };
    }
    default:
      return { kind: "unavailable", status: res.status };
  }
}
